import React, { useState, useRef, useEffect } from 'react';
import MiniCalendar from '../../../../../tools/leader/components/MiniCalendar';
import { formatDate } from '../../../../../../utils/dateUtils';
import '../../../../../../components/shared/filterStyles.css';

/**
 * JSDoc for UnifiedDateRangePicker component.
 * 
 * @component
 * @param {Object} props - The component props.
 * @param {Date|string|null} props.startDate - The starting date of the selected range.
 * @param {Date|string|null} props.endDate - The ending date of the selected range.
 * @param {Function} props.onChange - Callback triggered when the range changes. Receives ({ start, end }) => void.
 * @returns {React.ReactElement} The date range picker element.
 */
export default function UnifiedDateRangePicker({ startDate, endDate, onChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSelect = (start, end) => {
        onChange({ start, end });
        // Auto-close only if both are selected
        if (start && end) {
            setIsOpen(false);
        }
    };

    const getLabel = () => {
        if (!startDate && !endDate) return 'Zeitraum auswählen';
        if (startDate && endDate) return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        if (startDate) return `Ab ${formatDate(startDate)}`;
        return `Bis ${formatDate(endDate)}`;
    };

    return (
        <div className="co-filter-group" ref={containerRef} style={{ position: 'relative' }}>
            <label>Zeitraum</label>
            <button
                type="button"
                className="co-filter-dropdown"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    cursor: 'pointer',
                    textAlign: 'left'
                }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{getLabel()}</span>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>▼</span>
            </button>
            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        zIndex: 1000,
                        marginTop: 4,
                        padding: 12,
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: 12,
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <MiniCalendar
                        dualMonth={true}
                        start={startDate}
                        end={endDate}
                        onSelect={handleSelect}
                    />
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                        <button
                            type="button"
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#4f46e5',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: 600
                            }}
                            onClick={() => {
                                onChange({ start: '', end: '' });
                                setIsOpen(false);
                            }}
                        >
                            Zeitraum leeren
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
